import { useState, useEffect, useCallback } from 'react'

interface CacheInfo {
  name: string
  size: number
  entries: number
}

interface UseCacheReturn {
  caches: CacheInfo[]
  isLoading: boolean
  error: string | null
  clearCache: (cacheName: string) => Promise<void>
  clearAllCaches: () => Promise<void>
  refreshCaches: () => Promise<void>
}

export const useCache = (): UseCacheReturn => {
  const [caches, setCaches] = useState<CacheInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getCacheInfo = useCallback(async (): Promise<CacheInfo[]> => {
    if (!('caches' in window)) {
      throw new Error('Cache API not supported')
    }

    const cacheNames = await window.caches.keys()
    const cacheInfos: CacheInfo[] = []

    for (const cacheName of cacheNames) {
      const cache = await window.caches.open(cacheName)
      const keys = await cache.keys()
      let totalSize = 0

      for (const request of keys) {
        const response = await cache.match(request)
        if (response) {
          const blob = await response.blob()
          totalSize += blob.size
        }
      }

      cacheInfos.push({
        name: cacheName,
        size: totalSize,
        entries: keys.length
      })
    }

    return cacheInfos
  }, [])

  const refreshCaches = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const cacheInfos = await getCacheInfo()
      setCaches(cacheInfos)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get cache info')
    } finally {
      setIsLoading(false)
    }
  }, [getCacheInfo])

  const clearCache = useCallback(async (cacheName: string) => {
    setIsLoading(true)
    setError(null)
    
    try {
      await window.caches.delete(cacheName)
      await refreshCaches()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear cache')
    } finally {
      setIsLoading(false)
    }
  }, [refreshCaches])

  const clearAllCaches = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const cacheNames = await window.caches.keys()
      await Promise.all(cacheNames.map(name => window.caches.delete(name)))
      await refreshCaches()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear all caches')
    } finally {
      setIsLoading(false)
    }
  }, [refreshCaches])

  useEffect(() => {
    refreshCaches()
  }, [refreshCaches])

  return {
    caches,
    isLoading,
    error,
    clearCache,
    clearAllCaches,
    refreshCaches
  }
}

// 格式化文件大小
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 检查是否支持Service Worker
export const isServiceWorkerSupported = (): boolean => {
  return 'serviceWorker' in navigator
}

// 检查是否支持Cache API
export const isCacheAPISupported = (): boolean => {
  return 'caches' in window
}

// 获取网络状态
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [connectionType, setConnectionType] = useState<string>('unknown')

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // 检查连接类型
    if ('connection' in navigator) {
      const connection = (navigator as any).connection
      setConnectionType(connection.effectiveType || 'unknown')
      
      const handleConnectionChange = () => {
        setConnectionType(connection.effectiveType || 'unknown')
      }
      
      connection.addEventListener('change', handleConnectionChange)
      
      return () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
        connection.removeEventListener('change', handleConnectionChange)
      }
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { isOnline, connectionType }
}